import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../services/api';

/**
 * The owner's half of a device-password reset.
 *
 * The device's half already exists (desktop: device_reset.rs; phones: the lock
 * screen). The protocol is deliberately lopsided: the device invents a code and
 * shows it on its own screen, the owner types it here, and only the DEVICE can
 * check it. The server never knows the answer, so neither this page nor the
 * server can clear a password without someone seeing that device's screen.
 *
 * Server states: pending (device hasn't picked it up) -> displaying (code on
 * screen) -> code_submitted (device is checking) -> completed | back to
 * displaying on a wrong code. Once finished, the status endpoint only reports
 * "not active" — so "not active" right after we sent a code means it worked,
 * and "not active" without one means it expired or was called off.
 */

type Props = {
  deviceId: string;
  name: string;
  platform?: string;
  onClose: () => void;
};

type Phase =
  | { k: 'starting' }
  | { k: 'waiting' }
  | { k: 'code'; attemptsLeft: number; wrong: boolean }
  | { k: 'checking' }
  | { k: 'done' }
  | { k: 'ended' }
  | { k: 'error'; why: string };

const POLL_MS = 3000;
// Same alphabet the devices draw from: no I, L, O or U.
const CODE_RE = /[^0-9ABCDEFGHJKMNPQRSTVWXYZ]/g;

const isPhone = (p?: string) => /ios|iphone|ipad|android/i.test(p || '');

const errText = (e: any, fallback: string) => {
  const msg = e?.response?.data?.detail;
  return typeof msg === 'string' ? msg : msg?.message || fallback;
};

export function DeviceResetPanel({ deviceId, name, platform, onClose }: Props) {
  const [phase, setPhase] = useState<Phase>({ k: 'starting' });
  const [code, setCode] = useState('');
  const [sending, setSending] = useState(false);
  const [waitedLong, setWaitedLong] = useState(false);
  const submitted = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const alive = useRef(true);
  const path = `/api/devices/${encodeURIComponent(deviceId)}/password-reset`;

  const poll = useCallback(async () => {
    if (!alive.current) return;
    let finished = false;
    try {
      const { data } = await api.get(path);
      if (!alive.current) return;
      if (!data?.active) {
        setPhase(submitted.current ? { k: 'done' } : { k: 'ended' });
        finished = true;
      } else if (data.state === 'pending') {
        setPhase({ k: 'waiting' });
      } else if (data.state === 'displaying') {
        // Back to displaying after we sent a code means the device said no.
        const wrong = submitted.current;
        submitted.current = false;
        setPhase({ k: 'code', attemptsLeft: data.attempts_left ?? 5, wrong });
        if (wrong) setCode('');
      } else if (data.state === 'code_submitted') {
        setPhase({ k: 'checking' });
      }
    } catch (e: any) {
      // A blip in the connection shouldn't end the reset; keep asking.
    }
    if (!finished && alive.current) timer.current = setTimeout(poll, POLL_MS);
  }, [path]);

  useEffect(() => {
    alive.current = true;
    (async () => {
      try {
        await api.post(path);
        poll();
      } catch (e: any) {
        setPhase({ k: 'error', why: errText(e, 'Could not start the reset. Please try again.') });
      }
    })();
    const slow = setTimeout(() => setWaitedLong(true), 12000);
    return () => {
      alive.current = false;
      if (timer.current) clearTimeout(timer.current);
      clearTimeout(slow);
    };
  }, [path, poll]);

  const normalized = code.toUpperCase().replace(CODE_RE, '').slice(0, 6);
  const shown = normalized.length > 3 ? `${normalized.slice(0, 3)}-${normalized.slice(3)}` : normalized;

  const submit = async () => {
    if (normalized.length !== 6 || sending) return;
    setSending(true);
    try {
      await api.post(`${path}/code`, { code: normalized });
      submitted.current = true;
      setPhase({ k: 'checking' });
    } catch (e: any) {
      setPhase({ k: 'error', why: errText(e, 'Could not send that code. Please try again.') });
    } finally {
      setSending(false);
    }
  };

  const cancel = async () => {
    alive.current = false;
    if (timer.current) clearTimeout(timer.current);
    try { await api.delete(path); } catch { /* it expires on its own anyway */ }
    onClose();
  };

  const finished = phase.k === 'done' || phase.k === 'ended' || phase.k === 'error';

  return (
    <div className="dev-reset" role="region" aria-label={`Reset device password for ${name}`}>
      <div className="dev-reset-head">
        <b>Reset device password</b>
        <span className="dev-reset-sub">{name}</span>
      </div>

      {phase.k === 'starting' && <p>Starting…</p>}

      {phase.k === 'waiting' && (
        <>
          <p>
            Waiting for <b>{name}</b> to show a code. Open LACloud on it — it needs to be switched on and
            connected. The code appears on its sign-in screen.
          </p>
          {waitedLong && isPhone(platform) && (
            <p className="dev-reset-note">
              Phones need the LACloud app update that adds password resets. If {name} hasn't installed it yet,
              it won't show a code. In that case, delete and reinstall LACloud on the phone to clear the
              password — copy anything saved inside LACloud off the phone first.
            </p>
          )}
        </>
      )}

      {phase.k === 'code' && (
        <>
          <p>
            Type the 6-character code shown on <b>{name}</b>'s screen.
          </p>
          {phase.wrong && (
            <p className="dev-reset-bad" role="alert">
              That code didn't match. {phase.attemptsLeft} {phase.attemptsLeft === 1 ? 'attempt' : 'attempts'} left.
            </p>
          )}
          <div className="dev-reset-row">
            <input
              className="auth-input dev-reset-code"
              autoFocus
              value={shown}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
              placeholder="ABC-DEF"
              aria-label="Code shown on the device"
              autoComplete="off"
              spellCheck={false}
              maxLength={7}
            />
            <button type="button" className="dev-btn" disabled={normalized.length !== 6 || sending} onClick={submit}>
              {sending ? '…' : 'Confirm'}
            </button>
          </div>
        </>
      )}

      {phase.k === 'checking' && <p>Checking the code on {name}…</p>}

      {phase.k === 'done' && (
        <p className="dev-reset-ok" role="status">
          Done. {name}'s device password is cleared. It will say that you reset it, and whoever uses it can set a
          new one in LACloud's settings.
        </p>
      )}

      {phase.k === 'ended' && (
        <p className="dev-reset-bad" role="status">This reset expired or was cancelled. Start it again if you still need it.</p>
      )}

      {phase.k === 'error' && <p className="dev-reset-bad" role="alert">{phase.why}</p>}

      <div className="dev-reset-row">
        {finished ? (
          <button type="button" className="dev-btn" onClick={onClose}>Close</button>
        ) : (
          <button type="button" className="dev-btn" onClick={cancel}>Cancel reset</button>
        )}
        {!finished && <span className="dev-reset-sub">Expires after 15 minutes.</span>}
      </div>
    </div>
  );
}
